import type {
  DashboardWidget,
  FieldFormat,
  KpiAppearance,
  QueryDefinition,
  QueryResult,
  SavedState,
  VisualizationConfig,
} from "./types";

export const CHART_COLORS = [
  "#0d9488",
  "#94a3b8",
  "#e6b353",
  "#76b5af",
  "#cbd5e1",
];
export const FIELD_FORMATS: FieldFormat[] = [
  "number",
  "currency",
  "percent",
  "ratio",
  "minutes",
  "hours",
];
export function kpiPreset(preset: KpiAppearance["preset"]): KpiAppearance {
  return {
    preset,
    alignment: "start",
    labelPosition: "above",
    valueSize: "medium",
    backgroundColor: preset === "highlighted" ? "#f7fdfc" : "#ffffff",
    borderColor: preset === "highlighted" ? "#5eead4" : "#e2e8f0",
    valueColor: preset === "highlighted" ? "#00665e" : "#0f172a",
  };
}
export function mergeVisualization(
  base: VisualizationConfig,
  override?: Partial<VisualizationConfig>,
  data?: QueryResult,
): VisualizationConfig {
  const config = { ...base, ...override };
  const fieldFormats = {
    ...Object.fromEntries(data?.fields.map((f) => [f.name, f.format]) ?? []),
    ...base.fieldFormats,
    ...override?.fieldFormats,
  };
  return {
    ...config,
    fieldFormats,
    fieldLabels: {
      ...Object.fromEntries(data?.fields.map((f) => [f.name, f.label]) ?? []),
      ...base.fieldLabels,
      ...override?.fieldLabels,
    },
    seriesColors: { ...base.seriesColors, ...override?.seriesColors },
    kpi: config.kpi
      ? { ...kpiPreset(config.kpi.preset), ...config.kpi }
      : undefined,
    format: fieldFormats[config.y[0]] ?? config.format,
  };
}
export function widgetReport(
  report: QueryDefinition,
  widget?: DashboardWidget,
): QueryDefinition {
  const visualization = mergeVisualization(
    report.visualization,
    widget?.visualization,
  );
  if (
    widget?.title !== undefined &&
    visualization.type === "kpi" &&
    widget.visualization?.fieldLabels?.[visualization.y[0]] === undefined
  )
    visualization.fieldLabels = {
      ...visualization.fieldLabels,
      [visualization.y[0]]: widget.title,
    };
  return {
    ...report,
    name: widget?.title ?? report.name,
    description: widget?.description ?? report.description,
    visualization,
  };
}
/** Apply exactly the same column order, labels and units to tables and exports. */
export function presentationData(
  data: QueryResult,
  config: VisualizationConfig,
): QueryResult {
  const columns =
    config.type === "table"
      ? (config.columns ?? data.columns)
      : config.type === "kpi"
        ? config.y.slice(0, 1)
        : [...new Set([config.x, ...config.y])];
  const selected = columns.filter((c) => data.columns.includes(c));
  return {
    ...data,
    columns: selected,
    fields: selected.flatMap((name) => {
      const field = data.fields.find((f) => f.name === name);
      return field
        ? [
            {
              ...field,
              label: config.fieldLabels?.[name] ?? field.label,
              format: config.fieldFormats?.[name] ?? field.format,
            },
          ]
        : [];
    }),
  };
}
export function newWidget(reportId: string): DashboardWidget {
  return { id: crypto.randomUUID(), reportId, width: "half" };
}
export interface WidgetOrigin {
  dashboardId: string;
  widgetId: string;
}
export function saveReportToState(
  state: SavedState,
  report: QueryDefinition,
  origin?: WidgetOrigin,
): SavedState {
  return {
    ...state,
    reports: state.reports.some((r) => r.id === report.id)
      ? state.reports.map((r) => (r.id === report.id ? report : r))
      : [...state.reports, report],
    dashboards: origin
      ? state.dashboards.map((d) =>
          d.id !== origin.dashboardId
            ? d
            : {
                ...d,
                widgets: d.widgets.map((w) =>
                  w.id === origin.widgetId ? { ...w, reportId: report.id } : w,
                ),
              },
        )
      : state.dashboards,
  };
}
const record = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === "object" && !Array.isArray(value);
const strings = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every((v) => typeof v === "string");
const color = (value: unknown): value is string =>
  typeof value === "string" && /^#[0-9a-f]{6}$/i.test(value);
/** Saved browser data is untrusted. Ignore invalid optional settings independently. */
export function normalizeVisualization(
  value: unknown,
): Partial<VisualizationConfig> {
  if (!record(value)) return {};
  const result: Partial<VisualizationConfig> = {};
  if (["kpi", "table", "bar", "line", "pie"].includes(String(value.type)))
    result.type = value.type as VisualizationConfig["type"];
  if (typeof value.x === "string") result.x = value.x;
  if (strings(value.y)) result.y = [...new Set(value.y)];
  if (FIELD_FORMATS.includes(value.format as FieldFormat))
    result.format = value.format as FieldFormat;
  if (value.target === null) result.target = null;
  if (typeof value.target === "number" && Number.isFinite(value.target))
    result.target = value.target;
  if (strings(value.columns)) result.columns = [...new Set(value.columns)];
  for (const key of ["showHeader", "showDescription", "showFooter"] as const)
    if (typeof value[key] === "boolean") result[key] = value[key];
  for (const key of ["fieldLabels", "fieldFormats", "seriesColors"] as const) {
    const map = value[key];
    if (record(map)) {
      const pairs = Object.entries(map).filter(
        ([, v]) =>
          typeof v === "string" &&
          (key === "seriesColors"
            ? color(v)
            : key === "fieldFormats"
              ? FIELD_FORMATS.includes(v as FieldFormat)
              : true),
      );
      if (key === "fieldFormats")
        result.fieldFormats = Object.fromEntries(pairs) as Record<
          string,
          FieldFormat
        >;
      else result[key] = Object.fromEntries(pairs) as Record<string, string>;
    }
  }
  if (record(value.kpi)) {
    const k = value.kpi;
    const normalized = kpiPreset(
      k.preset === "highlighted" ? "highlighted" : "neutral",
    );
    if (["start", "center", "end"].includes(String(k.alignment)))
      normalized.alignment = k.alignment as KpiAppearance["alignment"];
    if (["above", "below"].includes(String(k.labelPosition)))
      normalized.labelPosition =
        k.labelPosition as KpiAppearance["labelPosition"];
    if (["small", "medium", "large"].includes(String(k.valueSize)))
      normalized.valueSize = k.valueSize as KpiAppearance["valueSize"];
    for (const key of ["backgroundColor", "borderColor", "valueColor"] as const)
      if (color(k[key])) normalized[key] = k[key];
    result.kpi = normalized;
  }
  return result;
}
export function normalizeWidget(value: unknown): DashboardWidget | undefined {
  if (
    !record(value) ||
    typeof value.id !== "string" ||
    !value.id ||
    typeof value.reportId !== "string"
  )
    return;
  return {
    id: value.id,
    reportId: value.reportId,
    width: ["fifth", "third", "half", "full"].includes(String(value.width))
      ? (value.width as DashboardWidget["width"])
      : "half",
    ...(typeof value.title === "string" ? { title: value.title } : {}),
    ...(typeof value.description === "string"
      ? { description: value.description }
      : {}),
    ...(value.visualization
      ? { visualization: normalizeVisualization(value.visualization) }
      : {}),
  };
}

/** Store only intentional differences so report defaults continue to be inherited. */
export function presentationOverrides(
  base: VisualizationConfig,
  edited: VisualizationConfig,
): Partial<VisualizationConfig> {
  const result: Record<string, unknown> = {};
  for (const key of Object.keys(edited) as (keyof VisualizationConfig)[]) {
    if (["fieldLabels", "fieldFormats", "seriesColors"].includes(key)) {
      const entries = Object.entries(edited[key] ?? {}).filter(
        ([name, value]) =>
          value !== (base[key] as Record<string, unknown> | undefined)?.[name],
      );
      if (entries.length) result[key] = Object.fromEntries(entries);
    } else if (JSON.stringify(edited[key]) !== JSON.stringify(base[key]))
      result[key] = edited[key];
  }
  return result as Partial<VisualizationConfig>;
}
